/**
 * Sector map themes (docs/04 environment themes): every room of a sector
 * shares one identity — floor, wall mass, edge walls, cover rocks, a
 * landmark, floor decals, wall-dressing props, a burn hazard, the gate
 * colour and the ambient motes. Asset keys resolve via assets/manifest.ts.
 */
export type ThemeId = 'scrap' | 'core' | 'brood' | 'rim';

/** Ambient mote behaviour (render/ambient-motes.ts). */
export interface MoteStyle {
  /** Frame of `env/mote` (0 spark, 1 ember, 2 spore, 3 glitch) */
  readonly frame: number;
  readonly tint: number;
  /** Drift velocity ranges (px/s) */
  readonly vx: readonly [number, number];
  readonly vy: readonly [number, number];
  /** Twinkle speed (rad/s) — alpha breathes with it */
  readonly twinkle: number;
  /** Spin (rad/s) */
  readonly spin: number;
}

export interface ThemeDef {
  readonly id: ThemeId;
  readonly ground: string;
  readonly wallMass: string;
  /** Edge wall blobs (3 variants) */
  readonly wall: string;
  /** Cover rock cluster pieces (3 variants) */
  readonly rock: string;
  readonly landmark: string;
  /** Floor decals (flat — never look solid) */
  readonly decals: string;
  /** Raised clutter — ONLY placed in the wall band (readability rule) */
  readonly props: string;
  /** Burn pool look (lava / acid / rift energy — same mechanic) */
  readonly hazard: string;
  readonly pitTint: number;
  /** Shield-door colour */
  readonly gateTint: number;
  readonly mote: MoteStyle;
}

export const THEMES: Readonly<Record<ThemeId, ThemeDef>> = {
  // S1 Scrap Belt — riveted deck plates, scrap heaps, sparks raining
  scrap: {
    id: 'scrap',
    ground: 'env/ground-scrap', wallMass: 'env/wallmass-scrap', wall: 'env/wall-scrap',
    rock: 'env/rock-scrap', landmark: 'env/landmark-scrap',
    decals: 'env/decal-scrap', props: 'env/prop-scrap', hazard: 'env/lava-pool',
    pitTint: 0xb8c4c8, gateTint: 0xffb340,
    mote: { frame: 0, tint: 0xffc070, vx: [-24, 24], vy: [18, 46], twinkle: 7, spin: 0 },
  },
  // S2 Elemental Core — basalt with glowing veins, crystals, rising embers
  core: {
    id: 'core',
    ground: 'env/ground-core', wallMass: 'env/wallmass-core', wall: 'env/wall-core',
    rock: 'env/rock-core', landmark: 'env/landmark-core',
    decals: 'env/decal-core', props: 'env/prop-core', hazard: 'env/lava-pool',
    pitTint: 0xd8a890, gateTint: 0xff7a30,
    mote: { frame: 1, tint: 0xff9a48, vx: [-14, 14], vy: [-64, -26], twinkle: 4, spin: 0 },
  },
  // S3 Brood Depths — living membrane, egg sacs, drifting spores
  brood: {
    id: 'brood',
    ground: 'env/ground-brood', wallMass: 'env/wallmass-brood', wall: 'env/wall-brood',
    rock: 'env/rock-brood', landmark: 'env/landmark-brood',
    decals: 'env/decal-brood', props: 'env/prop-brood', hazard: 'env/acid-pool',
    pitTint: 0xa8d8a0, gateTint: 0x8ee86a,
    mote: { frame: 2, tint: 0xa8f07a, vx: [-16, 16], vy: [-14, 10], twinkle: 2, spin: 0.4 },
  },
  // S4 Anomaly Rim — void glass, crystal shards, glitching motes
  rim: {
    id: 'rim',
    ground: 'env/ground-rim', wallMass: 'env/wallmass-rim', wall: 'env/wall-rim',
    rock: 'env/rock-rim', landmark: 'env/landmark-rim',
    decals: 'env/decal-rim', props: 'env/prop-rim', hazard: 'env/rift-pool',
    pitTint: 0xc8b0ff, gateTint: 0xb07cff,
    mote: { frame: 3, tint: 0xd0b0ff, vx: [-8, 8], vy: [-8, 8], twinkle: 9, spin: 1.6 },
  },
};

/** Endless runs walk the themes in sector-sized stretches (7 rooms each). */
export const ENDLESS_THEME_CYCLE: ReadonlyArray<ThemeId> = ['scrap', 'core', 'brood', 'rim'];
export const ENDLESS_THEME_ROOMS = 7;
